import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useMyGroups, useTrip } from '../lib/db';
import { balances, myUnit } from '../lib/split';
import { money } from '../lib/money';
import { Empty, Loading, Note, Page, Section, UnitDot } from '../components/ui';
import NewTrip from './NewTrip';
import type { CurrencyCode, Group, TripData } from '../types';

/**
 * Each trip loads its own subcollections so the list can show a live balance.
 * Fine at family scale, and every read comes from the local cache after the
 * first open.
 */
function TripCard({ group, uid, onTotal }: {
  group: Group;
  uid: string;
  onTotal: (groupId: string, currency: CurrencyCode, net: number) => void;
}) {
  const { data } = useTrip(group.id);
  const net = data ? netFor(data, uid) : null;

  // Report upward in an effect, never during render — setting a parent's state
  // mid-render is a React error, not just a warning.
  useEffect(() => {
    if (net !== null) onTotal(group.id, group.currency, net);
  }, [net, group.id, group.currency, onTotal]);

  const label = net === null ? '' : net > 0 ? 'you are owed' : net < 0 ? 'you owe' : 'all settled';
  const cls = net === null ? 'zero' : net > 0 ? 'pos' : net < 0 ? 'neg' : 'zero';

  return (
    <Link className="trip" to={`/t/${group.id}`}>
      <div className="head">
        <div className="tb-grow">
          <div className="name">{group.name}</div>
          <div className="when">
            {[group.when, `${data?.expenses.length ?? 0} expenses`].filter(Boolean).join(' · ')}
          </div>
        </div>
      </div>
      <div className="foot">
        <div className="stack">
          {(data?.units ?? []).map((u) => <UnitDot key={u.id} unit={u} />)}
        </div>
        <div className="bal">
          <div className="k">{label}</div>
          <div className={`v amt ${cls}`}>
            {net === null ? '…' : net === 0 ? '—' : money(Math.abs(net), group.currency)}
          </div>
        </div>
      </div>
    </Link>
  );
}

function netFor(data: TripData, uid: string): number | null {
  const mine = myUnit(data.units, uid);
  if (!mine) return null;
  return balances(data.units, data.expenses, data.settlements)[mine.id] ?? 0;
}

export default function Trips() {
  const { user } = useAuth();
  const { groups, loading, error } = useMyGroups(user?.uid);
  const [newTrip, setNewTrip] = useState(false);
  const [totals, setTotals] = useState<Record<string, { currency: CurrencyCode; net: number }>>({});

  // Stable identity, and a no-op when nothing changed, so the effect above
  // cannot loop.
  const record = useCallback((groupId: string, currency: CurrencyCode, net: number) => {
    setTotals((t) =>
      t[groupId]?.net === net && t[groupId]?.currency === currency
        ? t
        : { ...t, [groupId]: { currency, net } });
  }, []);

  // Rupees and dollars are never added together. Each currency gets its own line.
  const byCurrency: Partial<Record<CurrencyCode, number>> = {};
  for (const g of groups) {
    const t = totals[g.id];
    if (t) byCurrency[t.currency] = (byCurrency[t.currency] ?? 0) + t.net;
  }
  const open = (Object.keys(byCurrency) as CurrencyCode[]).filter((c) => byCurrency[c] !== 0);

  return (
    <Page
      wordmark={<>evenhouse<em>.</em></>}
      subtitle="every house, even"
      fab={null}
      noFab
    >
      {loading && !groups.length ? (
        <Loading />
      ) : error ? (
        <Note>Couldn't load your trips: {error}</Note>
      ) : (
        <>
          {groups.length > 0 && <Hero open={open} byCurrency={byCurrency} count={groups.length} />}

          <Section
            label="Your trips"
            action={<button onClick={() => setNewTrip(true)}>New trip</button>}
          />

          {groups.length === 0 ? (
            <>
              <Empty title="No trips yet">
                Start one, add the families coming along, and share the invite link
                in your group chat.
              </Empty>
              <button className="btn" onClick={() => setNewTrip(true)}>Create your first trip</button>
            </>
          ) : (
            <div style={{ display: 'grid', gap: 11 }}>
              {groups.map((g) => (
                <TripCard key={g.id} group={g} uid={user!.uid} onTotal={record} />
              ))}
            </div>
          )}

          <div style={{ marginTop: 18 }}>
            <Note>
              Evenhouse settles between <b>families</b>, not people. Couples and parents share
              one balance, so nobody ends up owing their own spouse — and kids can count as a
              full share, half a share, or none at all.
            </Note>
          </div>
        </>
      )}

      {newTrip && <NewTrip onClose={() => setNewTrip(false)} />}
    </Page>
  );
}

function Hero({ open, byCurrency, count }: {
  open: CurrencyCode[];
  byCurrency: Partial<Record<CurrencyCode, number>>;
  count: number;
}) {
  if (open.length === 0) {
    return (
      <div className="hero" style={{ marginTop: 6 }}>
        <div className="k">Overall</div>
        <div className="v amt zero">All square</div>
        <div className="n">across {count} {count === 1 ? 'trip' : 'trips'} · nothing outstanding</div>
      </div>
    );
  }
  if (open.length === 1) {
    const c = open[0];
    const v = byCurrency[c]!;
    return (
      <div className="hero" style={{ marginTop: 6 }}>
        <div className="k">{v > 0 ? 'Overall, you are owed' : 'Overall, you owe'}</div>
        <div className={`v amt ${v > 0 ? 'pos' : 'neg'}`}>{money(Math.abs(v), c)}</div>
        <div className="n">across {count} {count === 1 ? 'trip' : 'trips'}</div>
      </div>
    );
  }
  return (
    <div className="hero multi" style={{ marginTop: 6 }}>
      <div className="k">Where you stand</div>
      <div className="cur-rows">
        {open.map((c) => {
          const v = byCurrency[c]!;
          return (
            <div className="cur-row" key={c}>
              <span className="cl">
                <b>{c === 'INR' ? 'Indian rupees' : 'US dollars'}</b>
                <span>{v > 0 ? 'owed to you' : 'you owe'}</span>
              </span>
              <span className={`cv amt ${v > 0 ? 'pos' : 'neg'}`}>{money(Math.abs(v), c)}</span>
            </div>
          );
        })}
      </div>
      <div className="n">each trip settles in its own currency</div>
    </div>
  );
}
